import { TinyColor } from '@ctrl/tinycolor';
import {
  Divider,
  Flex,
  Form,
  Select,
  Spin,
  Tag,
  theme,
  Tree,
  TreeSelect,
  type TreeSelectProps,
  Typography,
} from 'antd';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { type Key, type ReactNode, useEffect, useMemo, useState } from 'react';
import SyncSuspense from '../../../components/sync-suspense';
import { type Agency, type Customer, type MealCategory, Site } from '../../../entities';
import { useNotification } from '../../../hooks/use-notification';
import { useQuery } from '../../../jsonapi-provider';
import utils from '../../../utils/utils';

interface DisplayConfigNode {
  title: string;
  key: string;
  checkable?: boolean;
  children?: DisplayConfigNode[];
}

export const displayConfigCheckboxes: DisplayConfigNode[] = [
  {
    title: 'Show Summary',
    key: 'showSummary',
    children: [
      { title: 'Include Beverages', key: 'showSummary.includeBeverages' },
      { title: 'Include Supplies', key: 'showSummary.includeSupplies' },
      {
        title: 'Include Meal Items',
        key: 'showSummary.includeMealItems',
        children: [
          {
            title: 'Include Meal Items Ingredients',
            key: 'showSummary.includeMealItems.includeMealItemsIngredients',
          },
        ],
      },
    ],
  },
  {
    title: 'Show meals per site',
    key: 'showMealsPerSite',
    children: [
      { title: 'Include Beverages', key: 'showMealsPerSite.includeBeverages' },
      { title: 'Include Supplies', key: 'showMealsPerSite.includeSupplies' },
      {
        title: 'Include Meal Items',
        key: 'showMealsPerSite.includeMealItems',
        children: [
          {
            title: 'Include Meal Items Ingredients',
            key: 'showMealsPerSite.includeMealItems.includeMealItemsIngredients',
          },
        ],
      },
    ],
  },
  {
    title: 'Show meals per customer',
    key: 'showMealsPerCustomer',
    children: [
      { title: 'Include Beverages', key: 'showMealsPerCustomer.includeBeverages' },
      { title: 'Include Supplies', key: 'showMealsPerCustomer.includeSupplies' },
      {
        title: 'Include Meal Items',
        key: 'showMealsPerCustomer.includeMealItems',
        children: [
          {
            title: 'Include Meal Items Ingredients',
            key: 'showMealsPerCustomer.includeMealItems.includeMealItemsIngredients',
          },
        ],
      },
    ],
  },
  {
    title: 'Show packing',
    key: 'showPacking',
    checkable: false,
    children: [
      {
        title: 'Show prepacked meals',
        key: 'showPacking.showPrepacked',
      },
      {
        title: 'show bundled meals',
        key: 'showPacking.showBundled',
      },
    ],
  },
  {
    title: 'Include Site Orders Comments',
    key: 'includeSiteOrdersComments',
  },
  {
    title: 'Include Supplies Report',
    key: 'includeSuppliesReport',
  },
  {
    title: 'Include Specials',
    key: 'includeSpecials',
    children: [
      {
        title: 'Show specials per site',
        key: 'includeSpecials.includeSpecialsPerSite',
      },
      {
        title: 'Show labels of meals with specials',
        key: 'includeSpecials.showLabels',
      },
    ],
  },
];

export const buildSitesTree = (
  sites: Site[],
  setSiteElementParent: (value: Record<string, string>) => void
): TreeSelectProps['treeData'] => {
  const siteElementParent: Record<string, string> = {};
  const tree = Object.values(
    utils.groupBy(
      sites.filter((site) => site.agency && site.state && site.county && site.customer),
      ({ agency }) => agency.id!
    )
  ).map((sitesByAgency) => ({
    value: `A@${sitesByAgency[0].agency.id}`,
    title: sitesByAgency[0].agency.name,
    children: Object.values(
      utils.groupBy(sitesByAgency, ({ state, county }) => `${state}/${county}`)
    )
      .map((sitesByCounty) => {
        siteElementParent[
          `S@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}`
        ] = `A@${sitesByAgency[0].agency.id}`;
        return {
          value: `S@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}`,
          title: `${sitesByCounty[0].county}, ${sitesByCounty[0].state}`,
          children: Object.values(utils.groupBy(sitesByCounty, ({ customer }) => customer.id!))
            .map((sitesByCustomer) => {
              siteElementParent[
                `C@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}/${sitesByCustomer[0].customer.id}`
              ] = `S@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}`;
              return {
                value: `C@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}/${sitesByCustomer[0].customer.id}`,
                title: `${sitesByCustomer[0].customer.name}`,
                children: sitesByCustomer
                  .toSorted((a, b) => a.name.localeCompare(b.name))
                  .map((site) => {
                    siteElementParent[
                      site.id!
                    ] = `C@${sitesByAgency[0].agency.id}/${sitesByCounty[0].state}/${sitesByCounty[0].county}/${sitesByCustomer[0].customer.id}`;
                    return {
                      value: site.id,
                      title: site.name,
                    };
                  }),
              };
            })
            .toSorted((a, b) => a.title.localeCompare(b.title)),
        };
      })
      .toSorted((a, b) => a.title.localeCompare(b.title)),
  }));
  setSiteElementParent(siteElementParent);
  return tree;
};

export const getExclusiveIdsInTree = (ids: string[]): string[] =>
  ids.filter((id) => !(id.startsWith('A@') || id.startsWith('S@') || id.startsWith('C@')));

const PrepSheetFilterFields = (): ReactNode => {
  const formInstance = Form.useFormInstance();
  const notification = useNotification();
  const { token } = theme.useToken();
  const [siteElementParent, setSiteElementParent] = useState<Record<string, string>>({});

  const agencies = useQuery<Agency[]>([
    'agency',
    {
      sort: 'name',
      fields: { agency: ['name'] },
      page: {
        limit: 10000,
      },
    },
  ]);

  const customers = useQuery<Customer[]>([
    'customer',
    {
      filter: 'isInactive==false',
      include: ['sites.agency'],
      sort: 'name',
      fields: {
        customer: ['name', 'sites'],
        site: ['name', 'state', 'county', 'agency'],
        agency: ['name'],
      },
      page: {
        limit: 10000,
      },
    },
  ]);

  const mealCategories = useQuery<MealCategory[]>([
    'meal-category',
    {
      fields: {
        'meal-category': ['name'],
      },
      sort: 'sortOrder',
      page: {
        size: 10000,
      },
    },
  ]);

  const allSites: Site[] = useMemo(
    () =>
      customers.data
        ?.map((cust) =>
          cust.sites.map((site) => Object.assign(new Site(), site, { customer: cust }))
        )
        .flat() ?? [],
    [customers.data]
  );

  const sitesTreeData = useMemo(() => buildSitesTree(allSites, setSiteElementParent), [allSites]);

  useEffect(() => {
    customers.error &&
      notification.error({
        message: ' ',
        description: customers.error?.detail,
      });
  }, [customers.error]);

  const siteTagRender: TreeSelectProps['tagRender'] = ({ label, value }) => {
    const valueStr = value as string;
    if (formInstance.getFieldValue(['filters', 'site'])?.includes(siteElementParent[valueStr]))
      return <></>;
    if (valueStr?.startsWith('A@'))
      return (
        <Tag color={token.colorPrimary}>
          {agencies.data?.find((agency) => agency.id === valueStr.split('@')[1])?.name ??
            'UNKNOWN AGENCY'}
        </Tag>
      );
    else if (valueStr?.startsWith('S@') || valueStr?.startsWith('C@')) {
      const valueSplit = valueStr.split(/@|\//);
      return (
        <Tag className="site-tag" color="processing">
          <Typography
            style={{
              background: token.colorPrimary,
              color: token.colorPrimaryText,
            }}
          >
            {agencies.data?.find((agency) => agency.id === valueSplit[1])?.name ?? 'UNKNOWN AGENCY'}
          </Typography>
          <Typography
            style={{
              background: new TinyColor(token.colorPrimary).setAlpha(0.3).toHslString(),
            }}
          >
            {valueSplit[3]}, {valueSplit[2]}
          </Typography>
          {valueSplit[4] ? (
            <Typography
              style={{
                background: new TinyColor(token.colorPrimary).setAlpha(0.1).toHslString(),
              }}
            >
              {customers.data?.find((customer) => customer.id === valueSplit[4])?.name ??
                'UNKNOWN CUSTOMER'}
            </Typography>
          ) : (
            ''
          )}
        </Tag>
      );
    } else return <Tag color="processing">{label}</Tag>;
  };

  return (
    <SyncSuspense
      fallback={
        <Flex style={{ height: 300 }} justify="center" align="center">
          <Spin />
        </Flex>
      }
      condition={!customers.isLoading && !mealCategories.isLoading}
    >
      <Form.Item name={['filters', 'mealCategory']} label="Meal Category">
        <Select
          allowClear
          showSearch
          mode="multiple"
          options={mealCategories.data ?? []}
          placeholder="All Meal Categories"
          fieldNames={{ label: 'name', value: 'id' }}
        />
      </Form.Item>

      <Form.Item name={['filters', 'mainType']} label="Main Type">
        <Select
          allowClear
          showSearch
          mode="multiple"
          options={['Regular', 'Diabetic'].map((val) => ({ id: val, name: val }))}
          placeholder="All types"
          fieldNames={{ label: 'name', value: 'id' }}
        />
      </Form.Item>

      <Form.Item label="Sites" name={['filters', 'site']}>
        <TreeSelect
          allowClear
          showSearch
          treeCheckable
          disabled={customers.isLoading}
          loading={customers.isLoading}
          showCheckedStrategy={TreeSelect.SHOW_ALL}
          treeNodeFilterProp="title"
          listHeight={500}
          treeDefaultExpandedKeys={agencies.data?.map((agency) => `A@${agency.id!}`) ?? []}
          treeData={sitesTreeData}
          tagRender={siteTagRender}
          style={{ minWidth: 400 }}
          placeholder="All Sites"
        />
      </Form.Item>

      <Divider />

      <Form.Item
        name="enabledDisplayConfig"
        valuePropName="checkedKeys"
        style={{ marginBlockEnd: 0 }}
      >
        <Tree
          selectable={false}
          checkable
          defaultExpandAll={true}
          switcherIcon={({ expanded }) =>
            expanded ? (
              <ChevronDown width={23} height={23} />
            ) : (
              <ChevronRight width={23} height={23} />
            )
          }
          onCheck={(checked, info) => {
            let checkList = (checked as Exclude<typeof checked, Key[]>).checked;
            const checkedKey = info.node.key;
            if (!info.checked) {
              checkList = checkList.filter((x) => !x.toString().startsWith(checkedKey));
              // 'showPacking' is derived from its children, so drop it once none are left.
              if (!checkList.some((x) => x.toString().startsWith('showPacking.'))) {
                checkList = checkList.filter((x) => x !== 'showPacking');
              }
            } else {
              const newlyCheckedKeys = Array.from(
                checkedKey.toString().matchAll(/(?<=(\S+))(?:\.|$)/g),
                (x) => x[1]
              );
              checkList = Array.from(new Set([...checkList, ...newlyCheckedKeys]));
            }
            formInstance.setFieldValue('enabledDisplayConfig', checkList);
          }}
          checkStrictly
          treeData={displayConfigCheckboxes}
        />
      </Form.Item>
    </SyncSuspense>
  );
};

export default PrepSheetFilterFields;
